import {
  DropdownMenuOptionVariants,
  WorkflowNode,
} from "@validereinc/common-components";
import { Handle, NodeProps, Position } from "@xyflow/react";
import classNames from "classnames/bind";
import React, { useMemo } from "react";

import {
  findBranchContainingStep,
  isAssessTemplatedReportForCommentsStep,
  isChoiceStep,
  isParallelStep,
} from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/WorkflowConfigurationAddNodeDrawer.helpers";
import {
  AssessTemplatedReportForCommentsAction,
  ChoiceTask,
  SystemActionType,
  SystemActionTypeType,
  UserTaskType,
  WorkflowStepType,
  WorkflowUserTaskType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import { useWorkflowGraphState } from "../WorkflowGraphCanvas/WorkflowGraphStore";
import { useWorkflowGraphInteractions } from "../WorkflowGraphCanvas/useWorkflowGraphInteractions";
import { DebugTooltip } from "./DebugTooltip";
import styles from "./WorkflowGraphNode.module.scss";
import { WorkflowGraphNodeType } from "./types";
// Use getAllWorkflowSteps to include steps from parallel branches
const { getAllWorkflowSteps } = Workflows.logic;

const cx = classNames.bind(styles);

const WorkflowGraphNode = (props: NodeProps<WorkflowGraphNodeType>) => {
  const { data, id, selected, dragging, positionAbsoluteX, positionAbsoluteY } =
    props;

  const {
    selectedNode,
    isInEditMode,
    currentConfiguration,
    setStepIdToDelete,
  } = useWorkflowGraphState();
  const { onCopyStep } = useWorkflowGraphInteractions();

  const isDisabled = selectedNode?.data.variant === "trigger" && data.isDummy;
  const shouldShowCopyAction =
    (["generic", "system"].includes(data.variant) &&
      data.step?.type === WorkflowStepType.USER_TASK &&
      data.step?.task?.type !== UserTaskType.CHOICE) ||
    (data.step?.type === WorkflowStepType.SYSTEM_ACTION &&
      !(
        [
          SystemActionType.FORM_CHOICE,
          SystemActionType.VARIABLE_CHOICE,
          SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS,
        ] as SystemActionTypeType[]
      ).includes(data.step?.task?.type));

  const choiceStepNextStepIds = useMemo(() => {
    if (!currentConfiguration?.steps) {
      return {};
    }

    const allSteps = getAllWorkflowSteps(currentConfiguration.steps);

    return Object.fromEntries(
      Object.entries(allSteps)
        .filter((entry): entry is [string, WorkflowUserTaskType] =>
          isChoiceStep(entry[1])
        )
        .map(([stepId, step]) => {
          const choices = (step.task as ChoiceTask).choices
            .map((choice) => choice.next)
            .filter((next): next is string => next !== undefined);
          return [stepId, choices];
        })
    );
  }, [currentConfiguration?.steps]);

  const isStepInChoiceWithTwoChoices = useMemo(
    () =>
      Object.values(choiceStepNextStepIds).some(
        (choices) => choices.includes(id) && choices.length === 2
      ),
    [choiceStepNextStepIds, id]
  );

  const isReferencedByAssessTemplatedReport = useMemo(() => {
    if (!currentConfiguration?.steps) {
      return false;
    }

    const allSteps = getAllWorkflowSteps(currentConfiguration.steps);

    // Check if any assess templated report step references this step
    for (const step of Object.values(allSteps)) {
      if (
        step.type !== WorkflowStepType.PARALLEL &&
        isAssessTemplatedReportForCommentsStep(step)
      ) {
        const task = step.task as AssessTemplatedReportForCommentsAction;
        if (
          task.assessment_success_next === id ||
          task.assessment_failure_next === id
        ) {
          return true;
        }
      }
    }

    return false;
  }, [currentConfiguration?.steps, id]);

  const isStepInParallelWithTwoBranches = useMemo(() => {
    if (!currentConfiguration?.steps) {
      return false;
    }

    // Check if this step is in a parallel branch
    const branchInfo = findBranchContainingStep(
      currentConfiguration as unknown as Parameters<
        typeof findBranchContainingStep
      >[0],
      id
    );

    if (branchInfo) {
      const parallelStep = branchInfo.parallelStep;
      // Check if the parallel step has exactly 2 branches
      // and if this step is the start step of one of those branches
      if (
        isParallelStep(parallelStep) &&
        parallelStep.branches.length === 2 &&
        branchInfo.branch.start === id
      ) {
        return true;
      }
    }

    return false;
  }, [currentConfiguration?.steps, id]);

  const shouldShowDeleteAction =
    (data.variant === "trigger" && !data.isDummy) ||
    (data.variant !== "trigger" &&
      !(
        currentConfiguration?.start === id &&
        (data.step && isChoiceStep(data.step)
          ? !data.step?.end
          : data.step?.end)
      ) &&
      !isStepInChoiceWithTwoChoices &&
      !isReferencedByAssessTemplatedReport &&
      !isStepInParallelWithTwoBranches);

  const actions = useMemo(
    () =>
      isInEditMode
        ? [
            ...(shouldShowCopyAction
              ? [
                  {
                    label: "Copy",
                    onClick: () => onCopyStep(props.data.step),
                  },
                ]
              : []),
            ...(shouldShowDeleteAction
              ? [
                  {
                    label: "Delete",
                    variant: DropdownMenuOptionVariants.DANGER,
                    onClick: () => {
                      setStepIdToDelete(id);
                    },
                  },
                ]
              : []),
          ]
        : [],
    [
      isInEditMode,
      shouldShowCopyAction,
      shouldShowDeleteAction,
      props.data.step,
      id,
      setStepIdToDelete,
      onCopyStep,
    ]
  );

  return (
    <div
      style={{
        opacity: data?.isTranslucent ? 0.3 : 1,
        transition: "opacity 0.2s ease-out",
      }}
    >
      <WorkflowNode
        {...data}
        actions={actions}
        isDisabled={isDisabled}
      />
      <Handle
        type="target"
        position={Position.Top}
        className={cx("handle", "target")}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="a"
        className={cx("handle", "source")}
      />
      <DebugTooltip
        id={id}
        positionAbsoluteX={positionAbsoluteX}
        positionAbsoluteY={positionAbsoluteY}
        selected={selected}
        dragging={dragging}
        variant={data.variant}
        isTranslucent={data.isTranslucent}
      />
    </div>
  );
};
export default WorkflowGraphNode;
